﻿<template>
  <div v-if="isShowControl" class="ms-input ms-editor w-100" :class="{'ms-validate': isValidate}">
    <label v-if="label" class="label" :title="title">{{ label }}</label>
    <div
      class="flex-row relative"
      :class="[{error: errorMessage || wordNotCorrected?.length > 0, disabled: disabled}, hasBorder ? 'border' : '']"
      @dblclick="dblClickDisabledInput"
      @focusout="testBlurDisable"
    >
      <div v-if="leftIcon" :class="['icon24 icon left', leftIcon]" :title="errorProvider?.message"></div>

      <input
        v-if="trimModel"
        ref="input"
        v-model.trim="internalText"
        :title="internalText"
        :type="type"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        :class="{disabled: disabled}"
        v-on="listeners"
        :min="minValue"
        :max="maxValue"
      />

      <input
        v-else
        ref="input"
        v-model="internalText"
        :type="type"
        :title="internalText"
        class="ms-input-item flex w-100"
        :placeholder="placeholder"
        :disabled="disabled"
        :readOnly="readOnly"
        :maxlength="maxLength"
        :class="{disabled: disabled}"
        v-on="listeners"
        :min="minValue"
        :max="maxValue"
      />
      <div v-if="rightIcon" class="ic-right">
        <div :class="['icon24 icon', rightIcon]" @click="onClickRightIcon"></div>
      </div>

      <slot name="second-ic-right"> </slot>

      <div
        class="ic-show-correct-word"
        ref="correctWordSection"
        v-if="wordNotCorrected?.length > 0"
        @click="showCorrectWordBoard($event)"
      ></div>
    </div>

    <span v-if="errorMessage" class="error-text">
      {{ errorMessage }}
    </span>
  </div>
  <div v-else>{{ internalText }}</div>
</template>
<script>
import {defineComponent, ref, onMounted, computed, watch, nextTick, getCurrentInstance} from 'vue';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import {useValidateControl} from '@/setup/validateControl.js';
import {showListCorrectWord} from '@/commons/globalMessage';

export default defineComponent({
  name: 'MsInput',
  extends: msBaseComponent,
  props: {
    /**
     * V-model bắn ra ngoài binding 2 chiều
     */
    modelValue: {
      default: null,
    },
    /**
     * Hiện thị chũ mờ ở dưới ô input
     * Created by LTDAT(22.06.2020)
     */
    placeholder: {
      default: null,
      type: [Number, String],
    },
    // icon left
    leftIcon: {
      default: null,
      type: String,
    },
    // icon right
    rightIcon: {
      default: null,
      type: String,
    },
    // icon right
    type: {
      default: 'text',
      type: String,
    },
    // Giới hạn nhập của ô input
    maxLength: {
      default: null,
      type: Number,
    },
    // disabled
    disabled: {
      default: false,
      type: Boolean,
    },
    // Xác định xem input có border hay không?
    hasBorder: {
      default: true,
      type: Boolean,
    },
    /*
     * Hàm show ra câu thông báo riêng theo từng trường hợp
     * pvduy 18/03/2021
     * ví dụ: customWarring {rules:'required', mes: 'Tài sản này là của pvduy'}
     */
    customWarring: {
      type: Object,
      default: null,
    },
    /**
     * Có được click icon hay không?
     * NNLINH 27.06.2022
     */
    isClickRightIcon: {
      default: false,
      type: Boolean,
    },
    trimModel: {
      default: false,
      type: Boolean,
    },
    /** Regex nhập */
    regexMask: {
      default: null,
      type: RegExp,
    },
    /**
     * Tùy chọn có nhập dữ liệu input có tiền kiếm load grid không
     */
    filterSeachGrid: {
      type: Boolean,
      default: false,
    },
    /**
     * Field của value
     * @author nvthinh 10.1.2024
     */
    valueField: {
      type: String,
      default: '',
    },
    minValue: {
      type: Number,
    },
    maxValue: {
      type: Number,
    },
    // tooltip của label
    title: {
      type: String,
      default: null,
    },
    isCheckSpell: {
      type: Boolean,
      default: false,
    },
    /**
     * cho phép nhập liệu bằng giọng nói không
     * nhhoang1 15.05.2024
     */
    allowSpeechToText: {
      type: Boolean,
      default: false,
    },
    // biến đánh dấu có hiển thị control input hay không
    isShowControl: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['msKeydown', 'update:modelValue', 'focus', 'blur', 'change', 'msKeyup', 'clickRightIcon'],
  setup(props, {emit}) {
    // Variable
    const internalText = ref('');
    let focused = ref(false);
    let displayValue = null;
    let scrollWidth = 0;
    let blurTimeout = ref(null);

    const input = ref(null);
    const {errorMessage, validate, isValidate, clearValidate, wordNotCorrected} = useValidateControl({
      props,
    });

    const {proxy} = getCurrentInstance();

    // Method
    const hasTooltip = () => {
      if (scrollWidth > input?.value?.offsetWidth) {
        return true;
      }
      return false;
    };

    // Sự kiện focus vào ô input
    const onFocus = e => {
      focused.value = true;
      emit('focus', internalText.value, e);
    };

    // Sự kiên blur ra ngoài ô input
    const onBlur = async e => {
      focused.value = false;

      if (hasPress.value) {
        // Validate nghiệp vụ
        validate();
      }

      //  trim đầu đuôi
      //  NXHung 06.03.2024 confirm với chị nvanh sẽ trim đầu đuôi với các field input
      internalText.value = internalText.value?.toString().trim();
      emit('blur', internalText.value, e);
    };

    /**
     * Sự kiện Click icon bên phải ô Input
     * NNLINH 27.06.2022
     */
    const onClickRightIcon = () => {
      if (props.isClickRightIcon) {
        emit('clickRightIcon');
      }
    };

    // Reset lại giá trị về ban đầu
    const reset = () => {
      internalText.value = '';
      displayValue = null;
    };

    // Set giá trị cho ô input
    const setValue = value => {
      if (proxy.rules) {
        const rule = proxy.rules.find(x => x.name === 'pattern' && x.compareValue === 'phone');
        if (rule && value) {
          value = value.replace(/ /g, '').replace(/\./g, '');
        }
      }
      internalText.value = value;
    };

    // Lấy giá trị ô input
    const getValue = () => {
      return internalText.value;
    };

    /**
     * Hủy lan rộng event
     * HTHIEP 09.02.2022
     */
    const cancelEvent = e => {
      if (e) {
        if (typeof e.preventDefault === 'function') {
          e.preventDefault();
        }

        if (typeof e.stopPropagation === 'function') {
          e.stopPropagation();
        }

        if (typeof e.stopImmediatePropagation === 'function') {
          e.stopImmediatePropagation();
        }
      }
    };

    // Hooks
    // set internalText
    onMounted(() => {
      internalText.value = props.modelValue;
    });

    const hasPress = ref(false);

    /**
     * Lắng nghe event => thò ra ngoài
     * HTHIEP 11.02.2022: Làm mịn, migration sang vue 3
     */
    const listeners = computed(() => {
      return {
        blur: e => {
          const me = proxy;

          me.blurTimeout = setTimeout(() => {
            cancelEvent(e);

            me.onBlur(e);
          }, 150);
        },

        focus: e => {
          cancelEvent(e);
          onFocus(e);
        },

        change: e => {
          cancelEvent(e);
          emit('change', e, internalText.value);

          // Xử lý giá trị nhập < minValue
          props.type == 'number' &&
            typeof internalText.value == 'number' &&
            typeof props.minValue == 'number' &&
            props.minValue != null &&
            props.minValue != undefined &&
            internalText.value < props.minValue &&
            (internalText.value = props.minValue);

          // Xử lý giá trị nhập > maxValue
          props.type == 'number' &&
            typeof internalText.value == 'number' &&
            typeof props.maxValue == 'number' &&
            props.maxValue != null &&
            props.maxValue != undefined &&
            internalText.value > props.maxValue &&
            (internalText.value = props.maxValue);
        },
        keydown: e => {
          proxy.keyDownInput(e);
          hasPress.value = true;
        },
        keyup: e => {
          emit('msKeyup', e, internalText.value);
        },
        paste: e => {
          proxy.pasteInput(e);
        },
        input: e => {
          // Xử lý nhập quá maxLenght với input type = number
          props.type == 'number' &&
            props.maxLength &&
            typeof (internalText.value == 'number') &&
            internalText.value.toString().length > props.maxLength &&
            (internalText.value = internalText.value.toString().slice(0, props.maxLength));
        },
      };
    });

    let timeout = 0;
    /**
     * Lắng nghe xử lý update lại modelValue
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => internalText.value,
      value => {
        if (value !== props.modelValue) {
          emit('update:modelValue', value);
          scrollWidth = input.value.scrollWidth;
          // xử lý giá trị tìm kiếm grid khi nhập giá giá
          // nnninh 07/12/2023
          if (proxy.filterSeachGrid == true) {
            clearTimeout(timeout);
            timeout = setTimeout(() => {
              console.log(value);
              emit('filterGrid', value);
            }, 500);
          }
        }
      },
    );

    /**
     * Lắng nghe thay đổi modelValue set lại text cho input
     * HTHIEP 11.02.2022: Làm mịn, sửa lại từ vue 2 sang vue 3
     */
    watch(
      () => props.modelValue,
      value => {
        setValue(value);
      },
    );

    /**
     * Xử lý event keydown
     */
    function keyDownInput(e) {
      let check = true;

      if (!e.ctrlKey && e.key?.length == 1 && proxy.regexMask) {
        check = proxy.checkRegex(e, e.key);
      }

      if (check) {
        emit(proxy.$ms.constant.CustomEvent.MsKeydown, e, internalText.value);
      } else {
        e.preventDefault();
      }
    }

    /**
     * Kiểm tra Regex
     * @author nnnam - 13/10/2022
     */
    function checkRegex(e, char) {
      const input = e.target;
      const val = input.value;
      const len = val.length;
      const text = val.substring(0, input.selectionStart) + char;
      val.substring(input.selectionEnd, len);

      const isMatch = proxy.regexMask.test(text);
      return isMatch;
    }

    /**
     * Xử lý paste value
     */
    function pasteInput(e) {
      const char = e.clipboardData.getData('text');

      if (proxy.regexMask && !proxy.checkRegex(e, char)) {
        e.preventDefault();
      }
    }

    /**
     * double click vào ô input disabled để làm mất thuộc tính text-overflow
     * vqphong - 16/10/2023
     */
    const dblClickDisabledInput = () => {
      if (props.disabled) {
        let el = proxy.input;
        el.style.textOverflow = 'unset';
      }
    };

    /**
     * khôi phục lại thuộc tính text-overflow khi blur
     * vqphong - 16/10/2023
     */
    window.addEventListener('click', e => {
      if (props.disabled) {
        if (!proxy.input?.contains(e.target)) {
          let el = proxy.input;
          if (el) {
            el.style.textOverflow = 'ellipsis';
          }
        }
      }
    });

    /**
     * Show form sửa lỗi chính tả
     * pntoan 23.4.2024
     */
    const showCorrectWordBoard = event => {
      const position = {
        eventEle: event,
        correctWordCallback: correctWordCallback,
        sentenceOrigin: internalText.value,
        wordNotCorrectedOrigin: wordNotCorrected.value,
      };
      showListCorrectWord(position);
    };

    const correctWordCallback = data => {
      if (data) {
        internalText.value = data.internalText;
        wordNotCorrected.value = data.wordNotCorrected;
      }
    };

    const setValInput = val => {
      internalText.value = val;
    };

    return {
      internalText,
      focused,
      listeners,
      reset,
      hasTooltip,
      displayValue,
      input,
      getValue,
      errorMessage,
      validate,
      isValidate,
      clearValidate,
      onClickRightIcon,
      pasteInput,
      checkRegex,
      keyDownInput,
      dblClickDisabledInput,
      showCorrectWordBoard,
      wordNotCorrected,
      onBlur,
      blurTimeout,
      setValInput,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@/assets/scss/components/MsInput.scss';
.relative {
  position: relative;
}
.ic-show-correct-word {
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-top: 12px solid red;
  border-left: 12px solid transparent;
  cursor: pointer;
}
.ic-right {
  display: flex;
  align-items: center;
  width: $input-height;
  justify-content: center;
  border-left: 1px solid $secondary;
}
</style>
